import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getDeviceState, selectDeviceState, selectDeviceStateError, resetDeviceStateRetryCount, setDeviceStateError } from './deviceStateSlice';
import { setHudDimensions } from '../map/mapSlice';
import Signal from './Signal';
import { isDriverSignal, isPrioritySignal } from '../trafficInfra/SignalTypes';

import styles from './TrafficLightHUD.module.css';

const TrafficLightHUD = ({ activeLight, distance }) => {
    const dispatch = useDispatch();
    const deviceState = useSelector(selectDeviceState);
    const error = useSelector(selectDeviceStateError);
    const { t } = useTranslation();

    const hudRef = React.useRef(null);

    // Polling logic
    useEffect(() => {
        if (!activeLight) return;
        const deviceId = activeLight.liva_nro;

        // Reset state for new light
        dispatch(setDeviceStateError(null));
        dispatch(resetDeviceStateRetryCount());

        const fetchState = () => dispatch(getDeviceState(deviceId));
        fetchState(); // Initial fetch

        const interval = setInterval(fetchState, 1000);
        return () => clearInterval(interval);
    }, [activeLight, dispatch]);

    // Measure HUD dimensions
    useEffect(() => {
        if (!hudRef.current) return;

        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                const { width, height } = entry.contentRect;
                dispatch(setHudDimensions({ width, height }));
            }
        });

        resizeObserver.observe(hudRef.current);

        return () => {
            resizeObserver.disconnect();
            dispatch(setHudDimensions({ width: 0, height: 0 }));
        };
    }, []);

    if (!activeLight) return null;

    const allSignals = deviceState?.signalGroup || [];
    const deviceId = deviceState?.device || '';

    // Check if the loaded device state actually belongs to the active light
    // (deviceState.device is "TRE601", activeLight.liva_nro is "123")
    // Support shared devices: if activeLight is 602 (shared with 601), deviceState "TRE601" is valid.
    const validIds = [
        String(activeLight.liva_nro),
        ...(activeLight.yhteinen_koje || '').split(',').map(s => s.trim())
    ].filter(Boolean);

    const isActiveDeviceLoaded = validIds.some(id =>
        deviceId.toLowerCase().includes(id.toLowerCase())
    );

    // Topology matching result passed from MapView
    const matchedSignal = activeLight.matchedSignal; // { label: "A", full_label: "A1", bearing: 123 }
    const topologies = useSelector(state => state.trafficInfra.topologies);
    const topology = topologies[activeLight?.liva_nro];

    if (!isActiveDeviceLoaded) {
        return (
            <div className={styles.TrafficLightHUD} ref={hudRef}>
                <h3 className={styles.Title}>{activeLight.paikka}</h3>
                <div className={styles.Distance}>{Math.round(distance)} m</div>
                <div className={styles.Loading}>{t('hud.loading')}</div>
            </div>
        );
    }

    let candidateDisplaySignals = [];

    if (matchedSignal) {
        // If we have a confident match (e.g. "A"), show ONLY signals in that group.
        // Signal objects have 'name' like "A", "B", "C" (representing the group).
        const candidateSignals = allSignals.filter(s => isDriverSignal(s));

        // Match exact name OR name starting with matched label (e.g. K -> Ko)
        candidateDisplaySignals = candidateSignals.filter(s => {
            const name = s.name.toLowerCase();
            const label = matchedSignal.label.toLowerCase();

            // If we have an exact match (Explicit Maneuver), be strict.
            // e.g. Driving Straight -> Match "K", ignore "Ko".
            if (matchedSignal.isExactMatch) {
                return name === label;
            }

            // If browsing (No Maneuver), be loose.
            // e.g. Just looking -> Show "K" and "Ko".
            return name === label || name.startsWith(label);
        });

        if (candidateDisplaySignals.length === 0) {
            // Only warn if we actually expected to find something but didn't.
            console.warn(`Matched signal group ${matchedSignal.label} found no vehicle signals in live data. Available:`, candidateSignals.map(s => s.name).join(", "));
        }
    } else {
        candidateDisplaySignals = allSignals.filter(s => isDriverSignal(s));
    }

    const getComplementarySignal = (primarySignal) => {
        if (!topology || !topology.signals) return null;

        // Check if primary signal is a "partial" turn signal (ends in 1 or 2)
        // e.g. left1, right2. Full turns are "left", "right".
        const type = primarySignal.type || '';
        const isPartialTurn = /^(left|right)[12]$/i.test(type);

        if (!isPartialTurn) return null;

        // Find a straight signal with similar bearing (+/- 20 degrees)
        const signalsList = Object.values(topology.signals);
        const straightSignal = signalsList.find(ts => {
            if (ts.type !== 'straight') return false;

            // Calculate bearing difference
            const diff = Math.abs(ts.bearing - primarySignal.bearing);
            const normalizedDiff = diff > 180 ? 360 - diff : diff;

            return normalizedDiff <= 20;
        });

        if (!straightSignal) return null;

        // We found a straight signal in topology (e.g. B1). 
        // We need to find the live signal group for it (e.g. if B1 -> group B).
        // The group is usually the first letter(s) of the label.
        const groupKey = straightSignal.label ? straightSignal.label.match(/^([A-Za-z]+)/)?.[1] : null;
        if (!groupKey) return null;

        // Find the live signal object for this group
        const liveGroup = allSignals.find(s => s.name === groupKey);

        if (liveGroup) {
            // Return a synthetic signal object mixing live status with topology info
            return {
                ...liveGroup,
                type: 'straight',
                // Use a distinct key to avoid React key collisions if needed, though index is used usually
                idx: `complementary-${liveGroup.idx}`
            };
        }
        return null;
    };

    const enrichSignalWithType = (signal) => {
        // If this signal matches the specifically matched signal from SignalMatcher (e.g. G -> G4), use that type
        if (matchedSignal && matchedSignal.type && matchedSignal.label === signal.name) {
            return { ...signal, type: matchedSignal.type, bearing: matchedSignal.bearing };
        }

        if (!topology || !topology.signals) return signal;

        // Find matching signal in topology (e.g. signal.name "A" matches topology "A1", "A2"), the first matching head
        const signalsList = Object.values(topology.signals);

        // Strict match: Label must start with name AND follow with digit (e.g. K -> K1, K2).
        // This prevents "K" matching "Ko1".
        const topologySignal = signalsList.find(ts => {
            if (!ts.label && !ts.id) return false;
            const label = ts.label || ts.id;
            // Check if label is exactly the name OR starts with name + digit
            const regex = new RegExp(`^${signal.name}(\\d|$)`, 'i');
            return regex.test(label);
        });

        if (topologySignal) {
            return { ...signal, type: topologySignal.type, bearing: topologySignal.bearing };
        }
        return signal;
    };

    // --- Signal Enrichment and Deduplication ---

    // Helper to add signals uniquely
    // (Variables activeSignals and processedSignalNames are defined below)
    const activeSignals = [];
    const processedSignalKeys = new Set();
    const getSignalKey = (s) => `${s.name}_${s.type || 'unknown'}`;

    candidateDisplaySignals
        .filter(s => isDriverSignal(s) && !isPrioritySignal(s))
        .forEach(s => {
            const enriched = enrichSignalWithType(s);
            const key = getSignalKey(enriched);

            if (!processedSignalKeys.has(key)) {
                activeSignals.push(enriched);
                processedSignalKeys.add(key);
            }

            const complementarySignal = getComplementarySignal(enriched);
            if (complementarySignal) {
                const compKey = getSignalKey(complementarySignal);
                if (!processedSignalKeys.has(compKey)) {
                    activeSignals.push(complementarySignal);
                    processedSignalKeys.add(compKey);
                }
            }
        });

    // Sort to keep order consistent: A, B, C...
    activeSignals.sort((a, b) => a.name.localeCompare(b.name));

    const vehicleSignals = activeSignals;
    const prioritySignals = allSignals.filter(s => isPrioritySignal(s));

    return (
        <div className={styles.TrafficLightHUD} ref={hudRef}>
            <h3 className={styles.Title}>{activeLight.paikka}</h3>
            <div className={styles.Distance}>
                {Math.round(distance)} m
            </div>

            {error ? (
                <div className={styles.Error}>{t('hud.connection_error')}</div>
            ) : (
                <div className={styles.SignalsContainer}>
                    <div className={styles.SignalsRow}>
                        {vehicleSignals.length > 0 ? (
                            vehicleSignals.map((signal, idx) => (
                                <Signal key={`${signal.idx}-${idx}`} signal={signal} />
                            ))
                        ) : (
                            <div className={styles.Loading}>{t('hud.loading')}</div>
                        )}
                    </div>

                    {prioritySignals.length > 0 && (
                        <div className={styles.PriorityRow}>
                            {prioritySignals.map(signal => (
                                <Signal key={signal.idx} signal={signal} />
                            ))
                            }
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TrafficLightHUD;
